import type { Diagnostic, Programme, Rate } from './authoring';

/**
 * A day rate that a cheaper one covers completely, so it can never be charged.
 *
 * ---------------------------------------------------------------------------
 * THE DEFECT THIS EXISTS FOR
 * ---------------------------------------------------------------------------
 *
 * On the park's own camping card, two midweek day rates sit over the same
 * dates:
 *
 *     laagseizoentarief-midweek-4   €33   2026-08-31 -> 2026-09-14   (C period)
 *     laagseizoentarief-midweek-5   €29   2026-08-31 -> 2026-10-01   (A period)
 *
 * The engine tiles a stay with the cheapest legal combination, so the €33 rate
 * never wins anywhere: three September nights price at €87 where the park's own
 * sheet says €99. That was the largest camping increase of the season, and it
 * was silently not being charged.
 *
 * The weekend pair on the same card is split correctly — C runs to 14 September
 * and A begins there — so this is a window somebody forgot to close, not a
 * pricing decision.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS IS STRUCTURAL AND NOT ANOTHER SAMPLING CHECK
 * ---------------------------------------------------------------------------
 *
 * `findUnreachableRates` prices real stays and reports what never wins. Pointing
 * it at day rates was tried and is far too loud on a lifted card: the ported
 * arrangements TIE with the tiling by construction — the sheet defines the extra
 * days of a week arrangement as the arrangement divided by seven — so it
 * reported thirty-five "unreachable" rates on one card, and a check nobody can
 * read is a check nobody reads.
 *
 * Total containment is a different claim and can be decided by looking. Two
 * bands, one window inside the other, the inner one dearer, nothing else to
 * separate them: that is always a defect, and it is quiet.
 *
 * ---------------------------------------------------------------------------
 * WHAT IT DELIBERATELY WILL NOT SAY
 * ---------------------------------------------------------------------------
 *
 * Nothing about a card that STATES its roles. There, `flattenNightly` resolves
 * overlapping day bands so the narrower window wins outright — a dearer band
 * inside a cheaper one is the ordinary way to price a holiday week, and
 * reporting it would be wrong. It is only a defect where no flattening happens,
 * which is every card lifted from a legacy season.
 *
 * And nothing about two bands that differ in any way it cannot compare —
 * different arrival weekdays, different party requirements, a placement rule on
 * one and not the other. Those may genuinely both be reachable, and a false
 * positive here costs an operator more than a missed one.
 */

/** Rates whose windows and constraints make one of them unchargeable. */
export function findShadowedBands(programme: Programme): Diagnostic[] {
  // `flattenNightly` keys on the role, and one stated role puts the whole card
  // under narrower-wins layering.
  if (programme.rates.some((r) => Boolean(r.role))) return [];

  const bands = programme.rates.filter(isPlainDayRate);
  const out: Diagnostic[] = [];

  for (const dearer of bands) {
    for (const cheaper of bands) {
      if (dearer === cheaper) continue;
      if (priceOf(cheaper) >= priceOf(dearer)) continue;
      if (!covers(cheaper, dearer)) continue;
      if (!comparable(dearer, cheaper)) continue;

      out.push({
        severity: 'warning',
        code: 'shadowed-band',
        // The path the UI focuses, in the same shape every other diagnostic
        // uses, so clicking it lands on the rate rather than on the card.
        path: `rates.${programme.rates.indexOf(dearer)}.price.base`,
        message: `"${dearer.label ?? dearer.id}" can never be charged: `
          + `"${cheaper.label ?? cheaper.id}" costs ${money(priceOf(cheaper))} `
          + `over the whole of ${dearer.active.from} to ${dearer.active.until}, `
          + `and a stay is always priced with the cheapest combination. `
          + `Close the cheaper rate's window where this one begins, `
          + `as the weekend rates on this card already do.`,
      });
      break;
    }
  }
  return out;
}

/** A one-night rate with nothing clever about it. */
function isPlainDayRate(r: Rate): boolean {
  return r.nights === 1 && !r.extend && r.availability !== 'closed'
    && typeof r.price?.base === 'number';
}

const priceOf = (r: Rate): number => r.price!.base!;

/** Whether `outer`'s window contains `inner`'s entirely. */
function covers(outer: Rate, inner: Rate): boolean {
  return outer.active.from <= inner.active.from && outer.active.until >= inner.active.until;
}

/**
 * Whether the two are alike enough that price alone decides between them.
 *
 * Anything this cannot compare makes them incomparable rather than shadowed. A
 * false positive costs an operator a hunt for a defect that is not there.
 */
function comparable(a: Rate, b: Rate): boolean {
  const shape = (r: Rate) => JSON.stringify([
    r.arriveOn ?? null, r.departOn ?? null, r.stayArriveOn ?? null,
    r.minNights ?? null, r.requiresBookingTotalAtLeast ?? null,
  ]);
  return shape(a) === shape(b);
}

const money = (n: number): string => `€${n.toFixed(2).replace(/\.00$/, '')}`;
